import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

const AssignmentAuthoringContext = createContext(null);

const createSectionId = () =>
  `section-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeSection = (section) => {
  if (!section || typeof section !== "object") {
    return {
      id: createSectionId(),
      title: "New section",
      template: "custom",
      type: "custom",
      metadata: {},
    };
  }

  return {
    id: section.id || createSectionId(),
    title: section.title || "New section",
    template: section.template || section.type || "custom",
    type: section.type || section.template || "custom",
    metadata: section.metadata || {},
  };
};

export const AssignmentAuthoringProvider = ({
  children,
  initialSections = [],
}) => {
  const hasHydratedInitialSectionsRef = useRef(false);
  const [sections, setSections] = useState(() =>
    Array.isArray(initialSections)
      ? initialSections.map((section) => normalizeSection(section))
      : []
  );
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const hydrateInitialSections = useCallback(() => {
    if (hasHydratedInitialSectionsRef.current) {
      return;
    }

    if (Array.isArray(initialSections) && initialSections.length > 0) {
      setSections(initialSections.map((section) => normalizeSection(section)));
    }

    hasHydratedInitialSectionsRef.current = true;
  }, [initialSections]);

  const addSection = useCallback((section) => {
    hydrateInitialSections();
    setSections((prev) => [...prev, normalizeSection(section)]);
  }, [hydrateInitialSections]);

  const duplicateSection = useCallback((sectionId) => {
    hydrateInitialSections();
    setSections((prev) => {
      const index = prev.findIndex((section) => section.id === sectionId);
      if (index === -1) {
        return prev;
      }

      const source = prev[index];
      const duplicated = normalizeSection({
        ...source,
        id: createSectionId(),
        title: `${source.title} Copy`,
      });

      const next = [...prev];
      next.splice(index + 1, 0, duplicated);
      return next;
    });
  }, [hydrateInitialSections]);

  const deleteSection = useCallback((sectionId) => {
    hydrateInitialSections();
    setSections((prev) => prev.filter((section) => section.id !== sectionId));
  }, [hydrateInitialSections]);

  const reorderSections = useCallback((sourceIndex, destinationIndex) => {
    if (sourceIndex === destinationIndex) {
      return;
    }

    hydrateInitialSections();
    setSections((prev) => {
      if (
        sourceIndex < 0 ||
        destinationIndex < 0 ||
        sourceIndex >= prev.length ||
        destinationIndex >= prev.length
      ) {
        return prev;
      }

      const next = [...prev];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(destinationIndex, 0, moved);
      return next;
    });
  }, [hydrateInitialSections]);

  const updateSection = useCallback((sectionId, updater) => {
    hydrateInitialSections();
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) {
          return section;
        }

        const updates =
          typeof updater === "function" ? updater(section) : updater || {};

        return normalizeSection({ ...section, ...updates });
      })
    );
  }, [hydrateInitialSections]);

  const resetSections = useCallback((nextSections = []) => {
    hydrateInitialSections();
    setSections(Array.isArray(nextSections) ? nextSections.map(normalizeSection) : []);
  }, [hydrateInitialSections]);

  const contextValue = useMemo(
    () => ({
      sections,
      addSection,
      duplicateSection,
      deleteSection,
      reorderSections,
      updateSection,
      resetSections,
      isAutosaving,
      setIsAutosaving,
      validationErrors,
      setValidationErrors,
    }),
    [
      sections,
      addSection,
      duplicateSection,
      deleteSection,
      reorderSections,
      updateSection,
      resetSections,
      isAutosaving,
      validationErrors,
    ]
  );

  return (
    <AssignmentAuthoringContext.Provider value={contextValue}>
      {children}
    </AssignmentAuthoringContext.Provider>
  );
};

export const useAssignmentAuthoring = () => {
  const context = useContext(AssignmentAuthoringContext);

  if (!context) {
    throw new Error(
      "useAssignmentAuthoring must be used within an AssignmentAuthoringProvider"
    );
  }

  return context;
};

export default AssignmentAuthoringProvider;
